/**
 * Vendor "new scan" (migration 177): scan a box now and list the tags that
 * just arrived.
 *
 * Server: `mttr/app_api/routes/new_scan.py`, gated on `reader.vendor_scan`
 * and scoped to the caller's boxes server-side.
 */
import { apiFetch } from "./apiClient";
import type { ReaderJob, ReaderJobResult } from "./readerApi";

export interface NewArrivalItem {
  epc: string;
  sku: string | null;
  description: string | null;
  vendor_company_id: number | null;
}

/** Scoped to the caller by the server: platform sees every product; a vendor
 *  sees their own, with other suppliers' tags as `other_vendor_count`. */
export interface NewArrivals {
  new_count?: number;
  /** The previous completed scans "new" was measured against. */
  compared_scan_ids?: number[];
  items?: NewArrivalItem[];
  other_vendor_count?: number;
  unmatched_count?: number;
  unmatched_epcs?: string[];
  lookup_source?: string;
  lookup_error?: string | null;
}

export type NewScanJob = Omit<ReaderJob, "result"> & {
  result: (ReaderJobResult & { new_arrivals?: NewArrivals; after_complete_error?: string }) | null;
};

const base = (scu: string): string => `/api/boxes/${encodeURIComponent(scu)}/new-scan`;

export function startNewScan(scu: string, token: string): Promise<NewScanJob> {
  return apiFetch<NewScanJob>(base(scu), token, { method: "POST", body: "{}" });
}

export function fetchLatestNewScan(
  scu: string, token: string,
): Promise<{ job: NewScanJob | null }> {
  return apiFetch<{ job: NewScanJob | null }>(base(scu), token);
}

export function fetchNewScanJob(
  scu: string, jobId: number, token: string,
): Promise<NewScanJob> {
  return apiFetch<NewScanJob>(`${base(scu)}/${jobId}`, token);
}
